import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../config";
import { Box, Button, Typography, Stack, Chip, Paper } from "@mui/material";
import {
  createTheme,
  ThemeProvider,
  styled,
  keyframes,
  alpha,
} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";

import SaveIcon from "@mui/icons-material/Save";

// ─── Theme ────────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#2563EB", light: "#DBEAFE", dark: "#1D4ED8" },
    success: { main: "#059669" },
    background: { default: "#F0F2F8", paper: "#FFFFFF" },
    text: { primary: "#0F1117", secondary: "#4B5563", disabled: "#9CA3AF" },
  },
  typography: {
    fontFamily: "'Sora', 'Segoe UI', sans-serif",
    h2: { fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
});

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(() => ({
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EEF2FF 0%, #F0F2F8 40%, #E8EDF8 100%)",
  padding: "28px 20px 60px",
  fontFamily: "'Sora', sans-serif",
  "&::before": {
    content: '""',
    position: "fixed",
    top: -120,
    right: -120,
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37,99,235,0.06) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const ContentBox = styled(Box)({ maxWidth: 600, margin: "0 auto" });

const SectionCard = styled(Paper, {
  shouldForwardProp: (p) => p !== "delay",
})(({ theme, delay }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.04)",
  padding: "24px 28px",
  marginBottom: 20,
  animation: `${fadeUp} 0.5s ease both`,
  animationDelay: `${delay ?? 0}ms`,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(37,99,235,0.06), 0 12px 32px rgba(0,0,0,0.06)",
  },
}));

const CardLabel = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.9px",
  color: theme.palette.text.disabled,
  marginBottom: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "&::before": {
    content: '""',
    display: "inline-block",
    width: 3,
    height: 14,
    background: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

const BackBtn = styled(Button)(({ theme }) => ({
  borderRadius: 40,
  padding: "7px 18px 7px 12px",
  fontSize: 13,
  fontWeight: 600,
  textTransform: "none",
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha("#000", 0.12)}`,
  background: "#fff",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.primary.main, 0.05),
    borderColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    transform: "translateX(-2px)",
  },
}));

const PrimaryBtn = styled(Button)(() => ({
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  color: "#fff",
  borderRadius: 10,
  padding: "11px 28px",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  fontFamily: "'Sora', sans-serif",
  boxShadow: "0 4px 14px rgba(37,99,235,0.28)",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)",
    boxShadow: "0 6px 20px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": {
    background: "#E5E7EB",
    color: "#9CA3AF",
    boxShadow: "none",
  },
}));

const ToggleRow = styled(Box, {
  shouldForwardProp: (p) => p !== "checked" && p !== "accentcolor",
})(({ theme, checked, accentcolor }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "16px 18px",
  borderRadius: 12,
  border: `1.5px solid ${
    checked ? alpha(accentcolor, 0.35) : alpha("#000", 0.08)
  }`,
  background: checked ? alpha(accentcolor, 0.04) : alpha("#F0F2F8", 0.5),
  cursor: "pointer",
  userSelect: "none",
  transition: "all 0.2s ease",
  marginBottom: 12,
  "&:last-of-type": { marginBottom: 0 },
  "&:hover": {
    borderColor: checked ? accentcolor : alpha(accentcolor, 0.3),
    background: checked ? alpha(accentcolor, 0.07) : alpha(accentcolor, 0.03),
  },
}));

const ToggleIconBox = styled(Box)(({ accentcolor }) => ({
  width: 40,
  height: 40,
  borderRadius: 11,
  flexShrink: 0,
  background: alpha(accentcolor, 0.12),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s ease",
}));

const ToggleKnob = styled(Box, {
  shouldForwardProp: (p) => p !== "checked" && p !== "accentcolor",
})(({ checked, accentcolor }) => ({
  width: 46,
  height: 26,
  borderRadius: 13,
  position: "relative",
  flexShrink: 0,
  background: checked
    ? `linear-gradient(135deg, ${accentcolor}, ${alpha(accentcolor, 0.8)})`
    : alpha("#000", 0.14),
  transition: "all 0.22s ease",
  boxShadow: checked ? `0 2px 8px ${alpha(accentcolor, 0.4)}` : "none",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 4,
    left: checked ? 24 : 4,
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#fff",
    transition: "left 0.22s ease",
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
  },
}));

const Spinner = styled(Box)(() => ({
  width: 32,
  height: 32,
  border: "3px solid rgba(37,99,235,0.15)",
  borderTop: "3px solid #2563EB",
  borderRadius: "50%",
  animation: `${spin} 0.7s linear infinite`,
}));

const AvatarBox = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 18,
  flexShrink: 0,
  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 6px 18px rgba(37,99,235,0.3)",
}));

const SkeletonBlock = ({ w = "100%", h = 14, radius = 6 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: radius,
      background: alpha("#000", 0.07),
      flexShrink: 0,
      animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
    }}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminEditStudentPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();

  const [isStaff, setIsStaff] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [username, setUsername] = useState("");
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/admin/student/${studentId}/`)
      .then((res) => res.json())
      .then((data) => {
        setIsStaff(data.is_staff);
        setIsSuperUser(data.is_superuser);
        setUsername(data.username || "");
        setFetching(false);
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setFetching(false);
      });
  }, [studentId]);

  const updateStudent = () => {
    setSaving(true);
    fetch(`${API_URL}/admin/student/${studentId}/`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_staff: isStaff, is_superuser: isSuperUser }),
    })
      .then((res) => res.json())
      .then(() => navigate(-1))
      .catch((err) => {
        console.error("Error updating student:", err);
        setSaving(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <link
        href="https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap"
        rel="stylesheet"
      />

      <PageWrapper>
        <ContentBox>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 4,
              animation: `${fadeUp} 0.4s ease both`,
              flexWrap: "wrap",
            }}
          >
            <BackBtn
              startIcon={<ArrowBackIcon sx={{ fontSize: "15px !important" }} />}
              onClick={() => navigate(-1)}
            >
              Back
            </BackBtn>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: 22, sm: 27 },
                  color: "text.primary",
                  lineHeight: 1.1,
                }}
              >
                Edit Student
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.disabled",
                  mt: 0.3,
                  fontFamily: "'Sora', sans-serif",
                }}
              >
                ID: <strong style={{ color: "#4B5563" }}>{studentId}</strong>
              </Typography>
            </Box>
            <Chip
              label="Permissions"
              size="small"
              sx={{
                ml: "auto",
                fontFamily: "'Sora', sans-serif",
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                background: alpha("#2563EB", 0.1),
                color: "#2563EB",
                border: `1px solid ${alpha("#2563EB", 0.2)}`,
              }}
            />
          </Box>

          {/* ── Student info card ── */}
          <SectionCard delay={50}>
            {fetching ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "18px",
                    background: alpha("#000", 0.07),
                    flexShrink: 0,
                    animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <SkeletonBlock w="45%" h={18} />
                  <SkeletonBlock w="30%" h={13} />
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <AvatarBox>
                  <Typography
                    sx={{
                      fontSize: 26,
                      fontWeight: 800,
                      color: "#fff",
                      fontFamily: "'DM Serif Display', serif",
                    }}
                  >
                    {username?.[0]?.toUpperCase() || "?"}
                  </Typography>
                </AvatarBox>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "text.primary",
                      fontFamily: "'DM Serif Display', serif",
                      lineHeight: 1.2,
                    }}
                  >
                    {username || `Student #${studentId}`}
                  </Typography>
                  <Stack direction="row" spacing={0.8} sx={{ mt: 0.8 }}>
                    {isStaff && (
                      <Chip
                        label="Staff"
                        size="small"
                        sx={{
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 700,
                          fontSize: 10,
                          background: alpha("#059669", 0.1),
                          color: "#059669",
                          border: `1px solid ${alpha("#059669", 0.22)}`,
                        }}
                      />
                    )}
                    {isSuperUser && (
                      <Chip
                        label="Superuser"
                        size="small"
                        sx={{
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 700,
                          fontSize: 10,
                          background: alpha("#7C3AED", 0.1),
                          color: "#7C3AED",
                          border: `1px solid ${alpha("#7C3AED", 0.22)}`,
                        }}
                      />
                    )}
                    {!isStaff && !isSuperUser && (
                      <Chip
                        label="Regular User"
                        size="small"
                        sx={{
                          fontFamily: "'Sora', sans-serif",
                          fontWeight: 700,
                          fontSize: 10,
                          background: alpha("#000", 0.05),
                          color: "#9CA3AF",
                          border: `1px solid ${alpha("#000", 0.08)}`,
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Box>
            )}
          </SectionCard>

          {/* ── Permissions card ── */}
          <SectionCard delay={100}>
            <CardLabel>Permissions</CardLabel>

            {fetching ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {[...Array(2)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      borderRadius: "12px",
                      border: `1.5px solid ${alpha("#000", 0.07)}`,
                      padding: "16px 18px",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      background: alpha("#F0F2F8", 0.5),
                      animation: `${skeletonPulse} 1.5s ease-in-out infinite`,
                      animationDelay: `${i * 100}ms`,
                    }}
                  >
                    <SkeletonBlock w={40} h={40} radius={11} />
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <SkeletonBlock w="35%" h={14} />
                      <SkeletonBlock w="55%" h={11} />
                    </Box>
                    <SkeletonBlock w={46} h={26} radius={13} />
                  </Box>
                ))}
              </Box>
            ) : (
              <>
                {/* Is Staff toggle */}
                <ToggleRow
                  checked={isStaff}
                  accentcolor="#059669"
                  onClick={() => setIsStaff(!isStaff)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ToggleIconBox accentcolor="#059669">
                      <AdminPanelSettingsIcon
                        sx={{ fontSize: 20, color: "#059669" }}
                      />
                    </ToggleIconBox>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: isStaff ? "#059669" : "text.primary",
                          fontFamily: "'Sora', sans-serif",
                          lineHeight: 1.2,
                          transition: "color 0.2s",
                        }}
                      >
                        Staff Member
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "text.disabled",
                          fontFamily: "'Sora', sans-serif",
                          mt: 0.2,
                        }}
                      >
                        {isStaff
                          ? "This user has staff privileges"
                          : "Grant staff access to this user"}
                      </Typography>
                    </Box>
                  </Box>
                  <ToggleKnob checked={isStaff} accentcolor="#059669" />
                </ToggleRow>

                {/* Is Superuser toggle */}
                <ToggleRow
                  checked={isSuperUser}
                  accentcolor="#7C3AED"
                  onClick={() => setIsSuperUser(!isSuperUser)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ToggleIconBox accentcolor="#7C3AED">
                      <VerifiedUserIcon
                        sx={{ fontSize: 20, color: "#7C3AED" }}
                      />
                    </ToggleIconBox>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: isSuperUser ? "#7C3AED" : "text.primary",
                          fontFamily: "'Sora', sans-serif",
                          lineHeight: 1.2,
                          transition: "color 0.2s",
                        }}
                      >
                        Superuser
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "text.disabled",
                          fontFamily: "'Sora', sans-serif",
                          mt: 0.2,
                        }}
                      >
                        {isSuperUser
                          ? "This user has full admin rights"
                          : "Grant full superuser access"}
                      </Typography>
                    </Box>
                  </Box>
                  <ToggleKnob checked={isSuperUser} accentcolor="#7C3AED" />
                </ToggleRow>
              </>
            )}
          </SectionCard>

          {/* ── Save button ── */}
          {!fetching && (
            <Box
              sx={{
                animation: `${fadeUp} 0.5s ease both`,
                animationDelay: "160ms",
              }}
            >
              <Stack direction="row" spacing={1.5}>
                <PrimaryBtn
                  startIcon={
                    saving ? (
                      <Spinner
                        sx={{
                          width: 14,
                          height: 14,
                          borderWidth: "2px",
                          borderTopWidth: "2px",
                        }}
                      />
                    ) : (
                      <SaveIcon sx={{ fontSize: "16px !important" }} />
                    )
                  }
                  disabled={saving}
                  onClick={updateStudent}
                  fullWidth
                >
                  {saving ? "Saving…" : "Save Changes"}
                </PrimaryBtn>
                <Button
                  onClick={() => navigate(-1)}
                  fullWidth
                  sx={{
                    borderRadius: 2.5,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    border: `1px solid ${alpha("#000", 0.12)}`,
                    color: "text.secondary",
                    fontFamily: "'Sora', sans-serif",
                    "&:hover": { background: alpha("#000", 0.04) },
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}
        </ContentBox>
      </PageWrapper>
    </ThemeProvider>
  );
}
